import type { ReactNode } from 'react'

import { useEffect, useId, useRef, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { FadeLink } from '@/components/generals/fade-link'
import { bookingLinks } from '@/constants/site'

interface BookMenuProps {
    className?: string
}

export const BookMenu = ({ className }: BookMenuProps): ReactNode => {
    const [open, setOpen] = useState(false)
    const panelId = useId()
    const rootRef = useRef<HTMLDivElement | null>(null)
    const buttonRef = useRef<HTMLButtonElement | null>(null)

    useEffect(() => {
        if (!open) return

        const handlePointer = (event: PointerEvent) => {
            if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
        }

        const handleKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setOpen(false)
                buttonRef.current?.focus()
            }
        }

        document.addEventListener('pointerdown', handlePointer)
        document.addEventListener('keydown', handleKey)

        return () => {
            document.removeEventListener('pointerdown', handlePointer)
            document.removeEventListener('keydown', handleKey)
        }
    }, [open])

    return (
        <div ref={rootRef} className={cn('relative', className)}>
            <button
                ref={buttonRef}
                type='button'
                aria-expanded={open}
                aria-controls={panelId}
                onClick={() => setOpen(value => !value)}
                className={cn(
                    'label-mono inline-flex h-9 items-center rounded-full border px-5 transition-colors duration-500',
                    open ? 'border-brass bg-brass text-ink' : 'border-brass/60 text-star hover:border-brass hover:bg-brass hover:text-ink'
                )}
            >
                Book
            </button>

            <div
                id={panelId}
                className={cn(
                    'absolute right-0 top-[calc(100%+0.75rem)] w-[19rem] origin-top-right border border-hair bg-ink-2/95 p-2 backdrop-blur-md transition-all duration-500',
                    open ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-1 opacity-0'
                )}
            >
                <p className='label-mono px-3 pb-2 pt-2 text-star-3'>
                    Reserve your place
                </p>
                <ul>
                    {bookingLinks.map(link => (
                        <li key={link.href}>
                            <FadeLink
                                href={link.href}
                                onClick={() => setOpen(false)}
                                className='group flex items-start justify-between gap-4 border-t border-hair px-3 py-3.5 transition-colors duration-300 hover:bg-ink-3'
                            >
                                <span>
                                    <span className='block font-heading text-xl leading-tight text-star'>
                                        {link.label}
                                    </span>
                                    <span className='mt-1 block text-sm text-star-3'>
                                        {link.detail}
                                    </span>
                                </span>
                                <ArrowUpRight aria-hidden className='mt-1 size-4 shrink-0 text-brass transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5' strokeWidth={1.25} />
                            </FadeLink>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    )
}
